import React from 'react'
import { useState } from 'react'
import { PenIcon as EditIcon } from 'lucide-react'
import { faEye } from '@fortawesome/free-solid-svg-icons'

import Table from 'components/Table'
import Modal from 'components/Modal'
import { ViewVoucherModal } from './view-voucher'
import { UpdateVoucherModal } from './update-voucher'
import { ViewVoucherModalMainNZ } from './view-voucher-main-nz'
import { UpdateVoucherModalMainNZ } from './update-voucher-main-nz'


import { VouchersDataTypes } from 'schemas/campaign-voucher-schemas'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { isQSRHClient } from 'setups/controller'
import { projectClient } from 'constants/project'

const baseColumns = [
  'ID',
  'Code',
  'PLU',
  'Voucher Name',
  'Value',
  'Offer Type ID',
  'Min Spend Amount',
  'Offer Value',
  'MCA ID',
  'Issuer ID',
  'Issuer Name',
];

const columns = !isQSRHClient(projectClient)
  ? baseColumns
    .filter(
      (col) =>
        col !== 'PLU' &&
        col !== 'Min Spend Amount' &&
        col !== 'Offer Value'
    )
    .map((col) => (col === 'Offer Type ID' ? 'Offer ID' : col))
  : baseColumns;

interface Props {
  postData: VouchersDataTypes[]
}

const ResultTable = ({ postData }: Props): JSX.Element => {

  const [currentPage, setCurrentPage] = useState(1)
  const [showUpdateVoucherModal, setShowUpdateVoucherModal] = useState(false)
  const [showViewVoucherModal, setShowViewVoucherModal] = useState(false)
  const [selectedVoucherId, setSelectedVoucherId] = useState<number | null>(null)
  const [expandedVoucherId, setExpandedVoucherId] = useState<number | null>(null)

  function handlePageClick({ selected }: { selected: number }): void {
    setCurrentPage(selected + 1)
  }

  const handleRowClick = (voucherId: number) => {
    setExpandedVoucherId(expandedVoucherId === voucherId ? null : voucherId)
  }

  return (
    <>
      <Table
        data={postData}
        columns={columns}
        pageCount={0}
        showActions
        currentPage={currentPage}
        perPage={10}
        onPageChange={handlePageClick}
        itemCount={postData.length}
      >
        {postData.map((voucher) => (
          <React.Fragment key={voucher.voucherId}>

            <tr
              className=" text-black bg-white cursor-pointer hover:bg-gray-600"
              onClick={() => handleRowClick(voucher.voucherId)}
            >
              <td className="py-4 pl-4">
                <p className="tracking-wider">{voucher.voucherId}</p>
              </td>
              <td className="py-4 pl-4">
                <p className="tracking-wider">{voucher.voucherCode || "---"}</p>
              </td>
              {isQSRHClient(projectClient) && (
                <td className="py-4 pl-4">
                  <p className="tracking-wider">{voucher.pluCode || "---"}</p>
                </td>
              )}
              <td className="py-2 pl-4 max-w-0 overflow-hidden whitespace-nowrap overflow-ellipsis">
                <p className="tracking-wider">{voucher.voucherName || "---"}</p>
              </td>
              <td className="py-2 pl-4">
                <p className="tracking-wider">{voucher.voucherValue}</p>
              </td>
              {isQSRHClient(projectClient) ? (
                <td className="py-4 pl-4">
                  <p className="tracking-wider">{voucher.offerTypeId || "---"}</p>
                </td>
              ) : (
                <td className="py-2 pl-4">
                  <p className="tracking-wider">{voucher.offerId || "---"}</p>
                </td>
              )}
              {isQSRHClient(projectClient) && (
                <td className="py-2 pl-4">
                  <p className="tracking-wider">{voucher.offerValue2 || "---"}</p>
                </td>
              )}
              {isQSRHClient(projectClient) && (
                <td className="py-2 pl-4">
                  <p className="tracking-wider">{voucher.offerValue3 || "---"}</p>
                </td>
              )}
              <td className="py-4 pl-4">
                <p className="tracking-wider">{voucher.mcaId || "---"}</p>
              </td>
              <td className="py-4 pl-4 overflow-hidden whitespace-nowrap overflow-ellipsis">
                <p className="tracking-wider">{voucher.voucherIssuerId}</p>
              </td>
              <td className="py-4 pl-4 max-w-xs overflow-hidden whitespace-nowrap overflow-ellipsis">
                <p className="tracking-wider">
                  {voucher.voucherIssuerName}
                </p>
              </td>
              {/* <td className="py-2 pl-6">
                <p className="tracking-wider">{DateTime.fromISO(voucher.startDate || '').toLocaleString(DateTime.DATE_MED)}</p>
              </td>
              <td className="py-2 pl-6">
                <p className="tracking-wider">{DateTime.fromISO(voucher.endDate || '').toLocaleString(DateTime.DATE_MED)}</p>
              </td> */}

              <td className="float-right py-2 pl-2 mr-4" onClick={e => e.stopPropagation()}>
                <button
                  className="p-2 pl-0 pr-6 text-black bg-blue-500 rounded hover:bg-blue-600"
                  onClick={() => {
                    setSelectedVoucherId(voucher.voucherId ?? null)
                    setShowViewVoucherModal(true)
                  }}
                >
                  <FontAwesomeIcon className="w-4 h-4" icon={faEye} />
                </button>
                <button
                  className="p-2 text-black bg-blue-500 rounded hover:bg-blue-600"
                  onClick={() => {
                    setSelectedVoucherId(voucher.voucherId ?? null)
                    setShowUpdateVoucherModal(true)
                  }}
                >
                  <EditIcon className="w-4 h-4" />
                </button>
              </td>
            </tr>
            {expandedVoucherId === voucher.voucherId && (
              <tr>
                <td colSpan={columns.length + 1} className="bg-gray-200 px-4 py-6">
                  <div className="whitespace-pre-line break-words text-gray-800">
                    <strong>Voucher Name:</strong> {voucher.voucherName || <span className="italic text-gray-400">No voucher name</span>}
                  </div>
                  <br />
                  <div className="whitespace-pre-line break-words text-gray-800">
                    <strong>Description:</strong> {voucher.description || <span className="italic text-gray-400">No description</span>}
                  </div>
                </td>
              </tr>
            )}
          </React.Fragment>
        ))}
      </Table>
      <Modal
        open={showViewVoucherModal}
        title="View Voucher"
        onClose={() => setShowViewVoucherModal(false)}
        noFooter
        modalSize="regular"
      >
        {selectedVoucherId &&
          (!isQSRHClient(projectClient) ? (
            <ViewVoucherModalMainNZ
              voucherId={selectedVoucherId}
              onCloseModal={() => setShowViewVoucherModal(false)}
            />
          ) : (
            <ViewVoucherModal
              voucherId={selectedVoucherId}
              onCloseModal={() => setShowViewVoucherModal(false)}
            />
          ))}
      </Modal>
      <Modal
        open={showUpdateVoucherModal}
        title="Update Voucher"
        onClose={() => setShowUpdateVoucherModal(false)}
        noFooter
        modalSize="regular"
      >
        {selectedVoucherId &&
          (!isQSRHClient(projectClient) ? (
            <UpdateVoucherModalMainNZ
              voucherId={selectedVoucherId}
              onCloseModal={() => setShowUpdateVoucherModal(false)}
            />
          ) : (
            <UpdateVoucherModal
              voucherId={selectedVoucherId}
              onCloseModal={() => setShowUpdateVoucherModal(false)}
            />
          ))}
      </Modal>
    </>
  )
}

export default ResultTable
